// @flow strict

import * as React from 'react';
import styles from './popover.css';
import visibilityFn from './visibility.jsx';
import visibilityStyles from './visibility.css';
import closeSvg from '../images/ic_close_24px.svg';
import iconStyles from '../icons.css';
import svgImageFn from '../svg-image.jsx';
import clickCatcherFn from './click-catcher.jsx';
import clickCatcherStyles from './click-catcher.css';

const Visibility = visibilityFn(
  visibilityStyles.visible,
  visibilityStyles.hidden
);
const CloseIcon = svgImageFn(
  iconStyles.mediumSquare,
  iconStyles.whiteIcon,
  closeSvg
);
const ClickCatcher = clickCatcherFn(
  clickCatcherStyles.tooltip,
  clickCatcherStyles.inactive
);

export type Props = {|
  ariaLabel: string,
  children: React.Node,
  dimmed: boolean,
  popoverId: string,
  popoverContent: string,
|};

const Popover: React.AbstractComponent<Props> = (props: Props) => {
  const [visible, setVisible] = React.useState(false);

  const popoverTargetId = `popover-target-button-${props.popoverId}`
  const popoverCloseButtonId = `popover-close-button-${props.popoverId}`

  // A similar pattern could be used to replace the ClickCatcher component; see TP-621544 for details
  const keydownListener = (event: KeyboardEvent) => {
    if (event.code === 'Escape') {
      hidePopover(event)
    } else if (event.code === 'Tab') {
      // note this is a bit of a naive handling of trapping focus in the popover; it ignores that the content could have focusable elements
      // in ALE, popover content is coming from the ALD dataset and is santized, so this naive implementation is sufficient
      const target = event.target
      const popoverTarget = document.getElementById(popoverTargetId)
      const popoverClose = document.getElementById(popoverCloseButtonId)
      if (event.shiftKey && target === popoverTarget && popoverClose) {
        popoverClose.focus()
      } else if (target === popoverClose && popoverTarget) {
        popoverTarget.focus()
      }
    }
  }
  const showPopover = () => {
    document.addEventListener('keydown', keydownListener)
    setVisible(true)
  };
  const hidePopover = (event:Event) => {
    document.removeEventListener('keydown', keydownListener)
    event.stopPropagation();
    setVisible(false);
    const popoverTarget = document.getElementById(popoverTargetId)
    if (popoverTarget) {
      popoverTarget.focus()
    }
  };

  return (
    <div className={styles.outerPopoverContainer}>
      <ClickCatcher active={visible} onClickOutside={hidePopover} />
      <button
        id={popoverTargetId}
        className={styles.popoverClickTarget}
        /* we need an `onClick` handler even when the `ClickCatcher` is open so that VoiceOver button press closes
           the popover */
        onClick={visible ? hidePopover : showPopover}
        aria-label={props.ariaLabel}
        aria-hidden={props.dimmed}
        disabled={props.dimmed}
        data-cy="tooltip-icon"
      >
        {props.children}
      </button>
      <Visibility visible={visible}>
        <div className={styles.popoverContentContainer}>
          <div className={styles.popover}>
            <div
              className={`nwea-ds-v1-typography-caption ${styles.popoverContent}`}
              data-cy="tooltip-text"
            >
              {props.popoverContent}
            </div>
            <button
              id={popoverCloseButtonId}
              aria-label="close"
              className={styles.popoverClose}
              onClick={hidePopover}
              data-cy="popover-close"
            >
              <CloseIcon />
            </button>
            <div className={styles.popoverBottomArrow}></div>
          </div>
        </div>
      </Visibility>
    </div>
  );
};

export default Popover;
